import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import { PaymentStatusBadge, StatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilter,
  resetFilters,
  getTransactions,
} from "store/slices/customersSlice";
import { ORDER_BY_OPTIONS, PAYMENT_STATUS, PLAN_TYPES } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { CurrencySymbol, formatUTCDate, getMinMaxDate } from "utils/utils";

const Transactions = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    loading2,
    transactions,
    filters: {
      customerTransactionKeyword,
      dates,
      orderBy,
      customerTransactionPaymentStatus,
    },
  } = useSelector((state) => state.Customers);
  const { noTransactions } = Icons || {};

  useEffect(() => {
    getAllData();
  }, []);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 100,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      userId: id ?? "",
      paymentStatus: isReset ? "" : customerTransactionPaymentStatus,
      keyword: isResetKeyword ? "" : customerTransactionKeyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getTransactions(payload));
  };
  const filtersList = [
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "customerTransactionPaymentStatus",
      placeholder: t("Payment Status"),
      type: "singleSelect",
      options: PAYMENT_STATUS,
    },
  ];
  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const columns = useMemo(
    () => [
      {
        header: t("Invoice Number"),
        key: "invoiceNumber",
      },
      {
        header: t("Amount"),
        key: "amount",
        render: (value) => (
          <p className="price_symbol">
            <CurrencySymbol width="12" height="14" />
            {value}
          </p>
        ),
      },
      {
        header: t("Plan Type"),
        key: "planType",
        render: (value) =>
          PLAN_TYPES?.find((option) => option?.value === value)?.name || "-",
      },
      {
        header: t("Payment Method"),
        key: "paymentMethod",
      },
      {
        header: t("Billing Cycle Start"),
        key: "billingInfo",
        render: (value) => (
          <div className="ltr_text">
            {formatUTCDate(value?.billingCycleStart)}
          </div>
        ),
      },
      {
        header: t("Billing Cycle End"),
        key: "billingInfo",
        render: (value) => (
          <div className="ltr_text">
            {formatUTCDate(value?.billingCycleEnd)}
          </div>
        ),
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Payment Status"),
        key: "paymentStatus",
        render: (value) => <PaymentStatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  return (
    <div className="p-4">
      <FilterBar
        keyword={customerTransactionKeyword}
        setKeyword={(value) =>
          dispatch(setFilter({ key: "customerTransactionKeyword", value }))
        }
        keywordPlaceholder={t("Search by invoice number")}
        filters={filtersList}
        selectedFilters={{
          customerTransactionKeyword,
          orderBy,
          customerTransactionPaymentStatus,
        }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />
      {loading2 && <TableShimmer rows={12} columns={8} />}

      {!loading2 && transactions?.length === 0 && (
        <NoDataFound title={t("No transactions found")} icon={noTransactions} />
      )}

      {!loading2 && transactions?.length > 0 && (
        <DataTable columns={columns} data={transactions} t={t} />
      )}
    </div>
  );
};

export default Transactions;
